import React from "react";
import styled from "styled-components/macro";
import { COLORS, CONSTANTS, FONTSIZE, FONTWEIGHT } from "helpers/designConstants";
import { shortSeasonLabel } from "helpers/catalogueSearchGrouping";

// A catalogue entry is {id, seasonKey}; this is its identity as a Set member.
export const entryKey = (recordId, seasonKey) => `${recordId}::${seasonKey}`;

/**
 * One chip per active season the product exists in. A chip maps to the record carrying that season;
 * clicking it selects that record AND that season stamp.
 *
 * Price and size range appear on the chips ONLY when they actually differ across the group, so the
 * common case (a plain carryover) stays quiet and a real difference is impossible to miss.
 */
const SeasonChipRow = ({
  chips,
  selectedSeasonKey,
  onSelectChip,
  priceVaries,
  sizesVaries,
  addedEntryKeys,
  language
}) => {
  if (chips == null || chips.length === 0) return null;

  return (
    <Row>
      {chips.map((chip) => {
        // Keyed by catalogue ENTRY (record + season), not by record: a carryover record carries all
        // of its seasons, so every one of its chips points at the same record. Keying by record id
        // marked all of them added the moment the product was added under any one season.
        const isAdded = addedEntryKeys?.has(entryKey(chip.record.id, chip.seasonKey));
        const label = shortSeasonLabel(chip, language);
        return (
          <Chip
            key={chip.seasonKey}
            isSelected={chip.seasonKey === selectedSeasonKey}
            isAdded={isAdded}
            onClick={() => onSelectChip(chip)}
          >
            <ChipSeason>{label}</ChipSeason>
            {priceVaries && <ChipDetail>{`$ ${chip.record.retailPrice}`}</ChipDetail>}
            {sizesVaries && <ChipDetail>{chip.record.displaySizeRange}</ChipDetail>}
            {isAdded && <AddedDot aria-label={`${label} added`} />}
          </Chip>
        );
      })}
    </Row>
  );
};

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 4px 5px 0;
`;

const Chip = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 3px 7px;
  cursor: pointer;
  border-radius: ${CONSTANTS.borderRadius};
  font-size: ${FONTSIZE.xsmall};
  text-transform: uppercase;
  border: 1px solid
    ${({ isSelected }) => (isSelected ? COLORS.select : COLORS.secondary)};
  background: ${({ isSelected }) => (isSelected ? COLORS.secondary : "transparent")};
  color: ${COLORS.text};
`;

const ChipSeason = styled.span`
  font-weight: ${FONTWEIGHT.heavy};
`;

const ChipDetail = styled.span`
  font-weight: ${FONTWEIGHT.medium};
  opacity: 0.85;
`;

const AddedDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${COLORS.green};
`;

export default SeasonChipRow;
